import { type ReactNode, useEffect } from 'react';
import { useFeedback26CoStore } from '@/stores/feedback-26-co';
import type { FeedbackForm, FeedbackSponsor, HackerFeedbacksShow } from '@/types';
import {
  type FeedbackEdition,
  FeedbackEditionProvider,
} from './components/edition-context';
import { FeedbackSubmitted } from './components/FeedbackSubmitted';
import { NavigationButtons } from './components/NavigationButtons';
import { ProgressBar } from './components/ProgressBar';
import { StepRouter } from './components/StepRouter';

// Which edition and person the persisted draft belongs to. 26-co stored the bare public
// id here, so its value stays unprefixed and a Bogotá draft in progress survives.
const FEEDBACK_PUBLIC_ID_KEY = 'platanus-hack-feedback-26-co-publicId';

function draftOwner(editionSlug: string, publicId: string) {
  return editionSlug === '26-co' ? publicId : `${editionSlug}:${publicId}`;
}

function formatGithub(value: string | null) {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/github\.com\/(.+)$/i);
  return match ? match[1].replace(/\/$/, '') : trimmed.replace(/^@/, '');
}

// /hacker/:publicId/hack-<edition>/feedback. The Next page fetched
// /api/feedback-26-co/check from a useEffect and rendered "Cargando..." until it answered;
// the controller loads it now (C6), so the wizard is on screen from the first paint and
// the two failure screens are driven by props.
export default function Show({
  publicId,
  error,
  feedback,
  sponsors,
  qualityLabels,
  editionSlug,
  eventName,
  homePath,
  asksHackRacer,
}: HackerFeedbacksShow) {
  const edition: FeedbackEdition = {
    editionSlug,
    eventName,
    homePath,
    asksHackRacer,
  };

  // Switching to a different person's (or edition's) link starts a new form rather than
  // resuming the last one out of localStorage.
  useEffect(() => {
    const owner = draftOwner(editionSlug, publicId);
    const storedOwner = window.localStorage.getItem(FEEDBACK_PUBLIC_ID_KEY);
    if (storedOwner !== owner) {
      const { clearStorage, resetForm } = useFeedback26CoStore.getState();
      clearStorage();
      resetForm();
      window.localStorage.setItem(FEEDBACK_PUBLIC_ID_KEY, owner);
    }
  }, [editionSlug, publicId]);

  return (
    <FeedbackEditionProvider value={edition}>
      <FeedbackShell>
        <Body
          publicId={publicId}
          error={error}
          feedback={feedback}
          sponsors={sponsors}
          qualityLabels={qualityLabels}
          homePath={homePath}
        />
      </FeedbackShell>
    </FeedbackEditionProvider>
  );
}

function Body({
  publicId,
  error,
  feedback,
  sponsors,
  qualityLabels,
  homePath,
}: {
  publicId: string;
  error: string | null;
  feedback: FeedbackForm | null;
  sponsors: FeedbackSponsor[];
  qualityLabels: Record<string, string>;
  homePath: string;
}) {
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-4">
        <div className="max-w-2xl text-center">
          <h1 className="mb-4 font-bold text-3xl text-destructive">Error</h1>
          <p className="mb-6 text-lg text-muted-foreground">{error}</p>
        </div>
        <a
          href={homePath}
          className="bg-primary px-6 py-3 font-bold text-background text-lg transition-all hover:scale-105"
        >
          Volver al inicio
        </a>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-4">
        <div className="max-w-2xl text-center">
          <h1 className="mb-4 font-bold text-3xl text-destructive">
            No encontrado
          </h1>
          <p className="mb-6 text-lg text-muted-foreground">
            No se encontró el perfil del hacker.
          </p>
        </div>
        <a
          href={homePath}
          className="bg-primary px-6 py-3 font-bold text-background text-lg transition-all hover:scale-105"
        >
          Volver al inicio
        </a>
      </div>
    );
  }

  if (feedback.alreadySubmitted) {
    return (
      <FeedbackSubmitted
        hackerName={feedback.hackerName}
        supabaseGift={feedback.supabaseGift}
        marketRank={feedback.marketRank}
      />
    );
  }

  return (
    <Wizard
      publicId={publicId}
      feedback={feedback}
      sponsors={sponsors}
      qualityLabels={qualityLabels}
    />
  );
}

function Wizard({
  publicId,
  feedback,
  sponsors,
  qualityLabels,
}: {
  publicId: string;
  feedback: FeedbackForm;
  sponsors: FeedbackSponsor[];
  qualityLabels: Record<string, string>;
}) {
  const { currentStep, isSubmitted } = useFeedback26CoStore();

  if (isSubmitted || currentStep === 'thank-you') {
    return (
      <FeedbackSubmitted
        hackerName={feedback.hackerName}
        supabaseGift={feedback.supabaseGift}
        marketRank={feedback.marketRank}
      />
    );
  }

  const github = formatGithub(feedback.hackerGithub);

  return (
    <div className="flex min-h-screen flex-col pt-8 pb-8">
      <div className="mx-auto mb-6 w-full max-w-3xl px-4">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-4 py-3 text-sm">
          <div className="text-foreground">
            {feedback.hackerName}{' '}
            {github && <span className="font-bold">(@{github})</span>}
          </div>
          {(feedback.teamSlug || feedback.projectName) && (
            <div className="text-muted-foreground">
              <span className="font-bold text-foreground">
                {feedback.teamSlug || 'sin-equipo'}
              </span>
              {feedback.projectName && (
                <span>
                  {' '}
                  | <span className="font-bold">{feedback.projectName}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <ProgressBar currentStep={currentStep} />

      <div className="flex-1 overflow-y-auto px-2 py-8">
        <StepRouter
          step={currentStep}
          mentor={feedback.mentor}
          sponsors={sponsors}
          qualityLabels={qualityLabels}
        />
      </div>

      <NavigationButtons
        currentStep={currentStep}
        publicId={publicId}
        hasMentor={!!feedback.mentor}
      />
    </div>
  );
}

// app/(hack)/hacker/[publicId]/hack-26-co/feedback/layout.tsx.
function FeedbackShell({ children }: { children: ReactNode }) {
  return (
    <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
      <div className="mx-auto max-w-3xl px-4">{children}</div>
    </div>
  );
}
